import debounce from "lodash/debounce";
import map from "lodash/map";
import ChatController from "PFApp/components/chat/chat_controller";
import { ChatList } from "PFApp/components/chat/chat_list";
import { CHAT_NOTIFICATION_CATEGORIES } from "PFApp/constants/notifications";
import { selectIsChatListOpen, useChatStore, useChatStoreActions } from "PFApp/stores/chat_store";
import { useForceUpdate } from "PFCore/helpers/use_force_update";
import { useNotificationsInvalidate } from "PFCore/hooks/queries/notifications/use_notifications_invalidate";
import radii from "PFTheme/tokens/radii";
import { useCallback, useEffect, useMemo } from "react";

import css from "./chats.module.scss";

const EXPANDED_WIDTH = 500;
const REGULAR_WIDTH = 320;
const MINIMIZED_WIDTH = 320;
const LIST_WIDTH = 320;
const NAV_WIDTH = 60;
const MARGIN = 10;

export const Chats = () => {
  const { renderChat, unrenderChat, minimizeChat, unMinimizeChat, selectChat, expandChat } =
    useChatStoreActions();

  const forceUpdate = useForceUpdate();

  const { invalidate: invalidateNotifications } = useNotificationsInvalidate();

  const invalidateChatNotifications = useCallback(
    () => invalidateNotifications({ categories: CHAT_NOTIFICATION_CATEGORIES }),
    [invalidateNotifications]
  );

  useEffect(() => {
    const debouncedForceUpdate = debounce(() => forceUpdate(), 500);
    window.addEventListener("resize", debouncedForceUpdate);
    return () => window.removeEventListener("resize", debouncedForceUpdate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ids = useChatStore((state) => state.ids);
  const expandedId = useChatStore((state) => state.expandedId);
  const minimizedIds = useChatStore((state) => state.minimizedIds);
  const selectedId = useChatStore((state) => state.selectedId);
  const isChatListOpen = useChatStore(selectIsChatListOpen);

  const isMobile = window.matchMedia("(max-width: 560px)").matches;

  const offsets = useMemo(() => {
    let offset = NAV_WIDTH + (isChatListOpen ? LIST_WIDTH : 0) + MARGIN;
    let result: number[] = [];

    if (!isMobile) {
      ids.forEach((id) => {
        result.push(offset);
        if (minimizedIds.includes(id)) {
          offset += MINIMIZED_WIDTH + MARGIN;
        } else if (expandedId === id) {
          offset += EXPANDED_WIDTH + MARGIN;
        } else {
          offset += REGULAR_WIDTH + MARGIN;
        }
      });

      const maxOffset = window.innerWidth - REGULAR_WIDTH;
      if (offset > maxOffset) {
        const mod = (maxOffset - offset) / ids.length;
        result = result.map((off, i) => Math.max(0, off + mod * i));
      }
    }
    return result;
  }, [ids, minimizedIds, expandedId, isChatListOpen, isMobile]);

  // hangouts-like stacking. "topmost" at top and z-index the lower the further it's from it.
  const topmostIndex = selectedId === null ? -1 : ids.indexOf(selectedId);
  const anyTopmostId = topmostIndex > -1;

  return (
    <div className={css.root}>
      {isChatListOpen && (
        <ChatList
          openConversationIds={ids}
          onOpenConversation={renderChat}
          style={{
            left: isMobile ? 0 : NAV_WIDTH,
            bottom: 0,
            position: "absolute",
            width: LIST_WIDTH,
            zIndex: 10,
            borderRadius: `0 ${radii.radiusMd}`
          }}
          onMinimize={minimizeChat}
          onUnMinimize={unMinimizeChat}
          onClearNotifications={invalidateChatNotifications}
        />
      )}
      {map(ids, (id, index) => (
        <ChatController
          key={id}
          style={{
            left: `${offsets[index] || 0}px`,
            bottom: 0,
            position: "absolute",
            width:
              minimizedIds.indexOf(id) !== -1
                ? MINIMIZED_WIDTH
                : expandedId === id
                  ? EXPANDED_WIDTH
                  : REGULAR_WIDTH,
            zIndex: 10 + (anyTopmostId ? ids.length - Math.abs(index - topmostIndex) : 0)
          }}
          conversationId={id}
          selected={selectedId === id}
          expandable={!window.matchMedia("(max-width: 560px)").matches}
          expanded={expandedId === id}
          isBelow={anyTopmostId ? topmostIndex > index : true}
          onMinimize={minimizeChat}
          onExpand={expandChat}
          onUnMinimize={unMinimizeChat}
          onSelect={selectChat}
          onClose={unrenderChat}
          onClearNotifications={invalidateChatNotifications}
          minimized={minimizedIds.indexOf(id) !== -1}
        />
      ))}
    </div>
  );
};
